import React, { useEffect, useState } from 'react'
import fetchEdgeTeamDetail, { resolveTeamIdFromAbbrev } from '../lib/api/nhlEdgeTeam'

function Percent({ value }: { value?: number }) {
  if (value == null) return <span>—</span>
  return <span>{(value * 100).toFixed(1)}%</span>
}

function Rank({ rank, total = 100 }: { rank?: number; total?: number }) {
  if (rank == null) return <span>—</span>
  const pct = Math.max(0, Math.min(100, ((total - rank + 1) / total) * 100))
  return <span>{pct.toFixed(0)}% ({rank}/{total})</span>
}

export default function TeamDetailPage(): JSX.Element {
  const [abbrev, setAbbrev] = useState('DET')
  const [season, setSeason] = useState('20252026')
  const [teamId, setTeamId] = useState<number | null>(null)
  const [team, setTeam] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError(null)

    async function load() {
      try {
        const id = await resolveTeamIdFromAbbrev(abbrev)
        if (!mounted) return
        if (!id) {
          setError('Impossible de résoudre l\'abbrev en teamId')
          setLoading(false)
          return
        }
        setTeamId(id)
        const data = await fetchEdgeTeamDetail(id, season, 2)
        if (!mounted) return
        // Normalize: response may contain { team: {...}, ...metrics }
        const normalized: any = {
          meta: data?.team ?? data,
          shotSpeed: data?.shotSpeed,
          skatingSpeed: data?.skatingSpeed,
          distanceSkated: data?.distanceSkated,
          sogSummary: data?.sogSummary,
          sogDetails: data?.sogDetails,
          zoneTimeDetails: data?.zoneTimeDetails,
          seasonsWithEdgeStats: data?.seasonsWithEdgeStats,
          points: data?.team?.points ?? data?.points,
        }
        setTeam(normalized)
      } catch (err: any) {
        setError(err?.message ?? String(err))
      } finally {
        if (mounted) setLoading(false)
      }
    }

    load()
    return () => {
      mounted = false
    }
  }, [abbrev, season])

  return (
    <div className="container mt-4">
      <h2>NHL Edge — Team Detail</h2>

      <div className="row mb-3">
        <div className="col-auto">
          <label className="form-label">Abbréviation équipe</label>
          <input className="form-control" value={abbrev} onChange={(e) => setAbbrev(e.target.value)} />
        </div>
        <div className="col-auto">
          <label className="form-label">Saison</label>
          <input className="form-control" value={season} onChange={(e) => setSeason(e.target.value)} />
        </div>
      </div>

      {loading && <div>Chargement…</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {team && (
        <div>
          <div className="card mb-3">
            <div className="card-body d-flex align-items-center">
              <div style={{ width: 80, height: 80, marginRight: 16 }}>
                {(() => {
                  const logo = team.meta?.teamLogo?.light ?? team.meta?.teamLogo ?? team.meta?.teamLogo?.dark;
                  return logo ? <img src={logo} alt="logo" style={{ maxWidth: '100%' }} /> : null;
                })()}
              </div>
              <div>
                <h4 className="card-title">{team.meta?.commonName?.default ?? team.meta?.commonName?.fr ?? team.meta?.teamName ?? team.meta?.name}</h4>
                <div>{team.meta?.abbrev ?? team.meta?.abbreviation}</div>
                <div>
                  {team.meta?.wins != null && team.meta?.losses != null
                    ? `${team.meta.wins}-${team.meta.losses}${team.meta.otLosses ? `-${team.meta.otLosses}` : ''}`
                    : null}
                </div>
                <div>Points: {team.points ?? team.meta?.points ?? '—'}</div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-header">Skating / Bursts</div>
                <div className="card-body">
                  <div>Speed max (mph): {team.skatingSpeed?.speedMax?.imperial ?? '—'}</div>
                  <div>Speed rank: <Rank rank={team.skatingSpeed?.speedMax?.rank} total={team.seasonsWithEdgeStats ? 100 : undefined} /></div>
                  <div>Bursts ≥22: {team.skatingSpeed?.burstsOver22?.value ?? '—'} (rank: <Rank rank={team.skatingSpeed?.burstsOver22?.rank} />)</div>
                  <div>Bursts 20–22: {team.skatingSpeed?.burstsOver20?.value ?? '—'} (rank: <Rank rank={team.skatingSpeed?.burstsOver20?.rank} />)</div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-header">Shot Speed</div>
                <div className="card-body">
                  <div>Top shot speed (mph): {team.shotSpeed?.topShotSpeed?.imperial ?? '—'}</div>
                  <div>Top shot rank: <Rank rank={team.shotSpeed?.topShotSpeed?.rank} /></div>
                  <div>Shot attempts /90: {team.shotSpeed?.shotAttemptsOver90?.value ?? '—'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">Distance Skated</div>
            <div className="card-body">
              <div>Total (km): {team.distanceSkated?.total?.metric ?? '—'}</div>
              <div>Rank: <Rank rank={team.distanceSkated?.total?.rank} /></div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">SOG Summary (zones)</div>
            <div className="card-body">
              {Array.isArray(team.sogSummary) && team.sogSummary.length > 0 ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Zone</th>
                      <th>Shots</th>
                      <th>Shots Rank</th>
                      <th>Goals</th>
                      <th>Goals Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.sogSummary.map((s: any, i: number) => (
                      <tr key={i}>
                        <td>{s.locationCode ?? s.location}</td>
                        <td>{s.shots ?? '—'}</td>
                        <td><Rank rank={s.shotsRank} /></td>
                        <td>{s.goals ?? '—'}</td>
                        <td><Rank rank={s.goalsRank} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div>Aucune donnée SOG</div>
              )}
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">Zone Time Details</div>
            <div className="card-body">
              <div>Offensive zone %: <Percent value={team.zoneTimeDetails?.offensiveZonePctg} /></div>
              <div>Offensive zone rank: <Rank rank={team.zoneTimeDetails?.offensiveZoneRank} /></div>
              <div>Defensive zone %: <Percent value={team.zoneTimeDetails?.defensiveZonePctg} /></div>
              <div>Neutral zone %: <Percent value={team.zoneTimeDetails?.neutralZonePctg} /></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
